import React, { useCallback, useMemo, useState } from 'react';
import ReusableModal from '../../ui/organisms/Modal/CustomModal';
import CustomTypography from '../../ui/atoms/CustomTypography';
import { Box, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomTable from '../../ui/organisms/Table/Table';
import { createColumnHelper } from '@tanstack/react-table';
import useHistory from '../MainComponent/hooks/useHistory';
import dayjs from 'dayjs';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import IconImages from '../../assets/icons/IconImages';
import safeview from '../../assets/gifs/safeview.gif';
import { AnyARecord } from 'dns';

type ViewHistoryProps = {
  isModalOpen: boolean;
  handleModalClose: () => void;
  question: any;
};

const ViewHistory = ({
  isModalOpen,
  handleModalClose,
  question,
}: ViewHistoryProps) => {
  const {
    history,
    isHistoryFetching,
    isHistoryLoading,
    historyError,
    getAuditTrail,
    isAuditTrailPending,
    auditTrailError,
    auditTrailData,
  } = useHistory({
    question_key: question,
  });

  const [expandedState, setExpandedState] = useState<{
    rowIndex: number | null;
    columnIds: Set<string>;
  }>({
    rowIndex: null,
    columnIds: new Set<string>(),
  });

  const handleCellClick = useCallback(
    (rowIndex: number, columnId: string, row: any) => {
      setExpandedState((prev) => {
        // Collapse if already expanded
        if (prev?.rowIndex === rowIndex) {
          const columnIds = new Set(prev?.columnIds);
          if (columnIds?.has(columnId)) {
            columnIds?.delete(columnId);
          } else {
            // Expand the new column
            columnIds?.add(columnId);
          }
          return { rowIndex, columnIds };
        }

        // Expand for a new row
        if (columnId === 'ParentSubmissionId') {
          getAuditTrail({
            question_id: history?.QuestionId,
            asset_id: history?.AssetId,
            parent_submission_id: row?.ParentSubmissionId,
          });
        }
        return { rowIndex, columnIds: new Set([columnId]) };
      });
    },
    [history, getAuditTrail]
  );

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<any>();
    return [
      columnHelper.accessor('AuditCompleteDate', {
        header: 'Audit Date',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: '#333',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue()
              ? dayjs(info?.getValue())?.format('MM/DD/YYYY h:mm A')
              : ''}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('CreatedBy', {
        header: 'Updater',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: info?.row?.original?.AuditCompleteDate ? '#333' : '#777',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue()}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('Answers', {
        header: 'Value',
        cell: (info) =>
          info?.getValue()?.map((value: string, index: number) => {
            const type = history?.QuestionType;
            const isDate =
              dayjs(value, 'MM/DD/YYYY', true).isValid() &&
              history?.QuestionType == 'Date' &&
              type == 'Date';

            return (
              <CustomTypography
                variant='subtitle2'
                key={index}
                sx={{
                  color: info?.row?.original?.AuditCompleteDate
                    ? '#333'
                    : '#777',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                }}
              >
                {isDate ? dayjs(value)?.format('MM/DD/YYYY') : value}
              </CustomTypography>
            );
          }),
      }),
      columnHelper.accessor('OrderNumber', {
        header: 'Work Order #',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: '#0075d6',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue()}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('WorkCode', {
        header: 'Code',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: info?.row?.original?.AuditCompleteDate ? '#333' : '#777',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue()}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('VendorCode', {
        header: 'Vendor',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: info?.row?.original?.AuditCompleteDate ? '#333' : '#777',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue()}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('SourceSystem', {
        header: 'Source System',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: info?.row?.original?.AuditCompleteDate ? '#333' : '#777',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue()}
          </CustomTypography>
        ),
      }),
      columnHelper.accessor('Media', {
        header: 'Images',
        cell: (info) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: info?.getValue()?.length > 0 ? 'pointer' : 'not-allowed',
            }}
          >
            <IconImages />
            <CustomTypography
              variant='subtitle2'
              sx={{
                color: info?.row?.original?.AuditCompleteDate ? '#333' : '#777',
                lineHeight: '1.42px',
                fontSize: '0.8rem',
                marginX: '1rem',
              }}
            >
              {info?.getValue()?.length || '0'}
            </CustomTypography>
          </Box>
        ),
      }),
      columnHelper.accessor('ParentSubmissionId', {
        header: 'History',
        cell: () => (
          <Button>
            <DescriptionOutlinedIcon sx={{ color: '#484848db' }} />
          </Button>
        ),
      }),
    ];
  }, [history]);

  const innerColumn = useMemo(() => {
    const innerColumnHelper = createColumnHelper<any>();
    return [
      innerColumnHelper.accessor('Decision', {
        header: 'Decision',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: '#333',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue()}
          </CustomTypography>
        ),
      }),
      innerColumnHelper.accessor('Reason', {
        header: 'Reason',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: '#333',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue() || ''}
          </CustomTypography>
        ),
      }),
      innerColumnHelper.accessor('Note', {
        header: 'Note',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: '#333',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue() || ''}
          </CustomTypography>
        ),
      }),
      innerColumnHelper.accessor('Answer', {
        header: 'Answer',
        cell: (info) => {
          const type = history?.QuestionType;
          const isDate =
            dayjs(info?.getValue(), 'MM/DD/YYYY', true).isValid() &&
            type == 'Date';
          return (
            <CustomTypography
              variant='subtitle2'
              sx={{
                color: '#333',
                lineHeight: '1.42px',
                fontSize: '0.8rem',
              }}
            >
              {isDate
                ? dayjs(info?.getValue()).format('MM/DD/YYYY')
                : info?.getValue()?.toLanguagePack()}
            </CustomTypography>
          );
        },
      }),
      innerColumnHelper.accessor('LastModifiedBy', {
        header: 'Modified By',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: '#333',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue() || ''}
          </CustomTypography>
        ),
      }),
      innerColumnHelper.accessor('LastModifiedDate', {
        header: 'Modified Date',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: '#333',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {dayjs(info?.getValue())?.format('MM/DD/YYYY h:mm A') || ''}
          </CustomTypography>
        ),
      }),
      innerColumnHelper.accessor('Active', {
        header: 'Active',
        cell: (info) => (
          <CustomTypography
            variant='subtitle2'
            sx={{
              color: '#333',
              lineHeight: '1.42px',
              fontSize: '0.8rem',
            }}
          >
            {info?.getValue() ? 'Yes' : 'No'}
          </CustomTypography>
        ),
      }),
    ];
  }, [history]);

  const renderSubRowComponent = useCallback(
    (row: any, columnId: string) => {
      if (columnId === 'ParentSubmissionId') {
        // Display audit trail for History
        return (
          <Box sx={{ padding: '10px' }}>
            {isAuditTrailPending || auditTrailError ? (
              <Box sx={{ textAlign: 'center' }}>
                <span hidden={auditTrailError == null}>Unable to Load</span>
                <img
                  src={safeview}
                  alt='Loading...'
                  hidden={isAuditTrailPending == false}
                />
              </Box>
            ) : (
              <CustomTable
                columns={innerColumn}
                data={auditTrailData?.QuestionAuditTrail || []}
              />
            )}
          </Box>
        );
      }

      if (columnId === 'Media') {
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              padding: 2,
              overflowX: 'auto',
            }}
          >
            {row.Media?.map((media: any, idx: number) => (
              <Box
                key={idx}
                sx={{
                  width: '30%', // Fixed width
                  height: '250px', // Adjusted height to accommodate the label
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #ddd',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#f0f0f0',
                }}
              >
                {/* Image */}
                <img
                  src={media?.WebFileName}
                  alt={media?.MediaLabel?.toLanguagePack() || 'Image'}
                  style={{
                    width: '100%',
                    height: '89%',
                    objectFit: 'cover',
                  }}
                  loading='lazy'
                />

                {/* Media Label */}
                <Box
                  sx={{
                    width: '100%',
                    padding: '4px',
                    backgroundColor: '#ffffff',
                    textAlign: 'justify',
                    borderTop: '1px solid #ddd',
                  }}
                >
                  <CustomTypography
                    variant='subtitle2'
                    sx={{
                      fontSize: '12px',
                      color: '#333',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {media?.MediaLabel?.toLanguagePack() || 'No Label'}
                  </CustomTypography>
                </Box>
              </Box>
            ))}
          </Box>
        );
      }

      return null;
    },
    [auditTrailData, innerColumn, isAuditTrailPending, auditTrailError]
  );

  const currentAnswer = useMemo(() => {
    const returnedValue = history?.History?.find((item: any) => {
      return (
        item?.Answers && item?.Answers?.length > 0 && history?.QuestionType
      );
    });

    if (returnedValue?.Answers?.length > 0) {
      const type = history?.QuestionType;
      const value = returnedValue.Answers[0];
      const isDate = dayjs(value, 'MM/DD/YYYY', true).isValid();
      switch (type) {
        case 'Date': {
          return isDate ? dayjs(value).format('MM/DD/YYYY') : value;
          break;
        }

        default: {
          return value;
        }
      }
    }
    return null;
  }, [history]);

  return (
    <ReusableModal
      isOpen={isModalOpen}
      onClose={handleModalClose}
      width={1200}
      height='80%'
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'space-between',
        }}
      >
        <Box sx={{ padding: '24px 32px', background: '#0075d6' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <CustomTypography
              variant='body1'
              sx={{
                fontWeight: 500,
                color: '#FFF',
                fontSize: '18px',
                lineHeight: '1.1',
                textTransform: 'capitalise',
              }}
            >
              QUESTION HISTORY
            </CustomTypography>
            <Button onClick={handleModalClose}>
              <CloseIcon sx={{ color: '#FFF' }} />
            </Button>
          </Box>
        </Box>
        <Box sx={{ padding: '30px' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'space-between',
            }}
          >
            {isHistoryFetching || isHistoryLoading || historyError ? (
              <Box
                sx={{
                  display: 'flex',
                  flex: '1',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <span hidden={historyError == null}>Unable to load</span>
                <img
                  src={safeview}
                  hidden={
                    isHistoryFetching == false && isHistoryLoading == false
                  }
                />
              </Box>
            ) : (
              <>
                <CustomTypography
                  variant='h4'
                  sx={{
                    fontSize: '18px',
                    fontWeight: '500',
                    lineHeight: '1.1',
                    color: 'inherit',
                  }}
                >
                  {question?.toLanguagePack()}
                </CustomTypography>
                <CustomTypography
                  variant='h4'
                  sx={{
                    fontSize: '14px',
                    fontWeight: '500',
                    lineHeight: '1.1',
                    color: 'inherit',
                    marginY: '0.8rem',
                  }}
                >
                  Current Property Value: {currentAnswer || ''}
                </CustomTypography>

                <CustomTable
                  columns={columns}
                  data={history?.History}
                  onCellClick={handleCellClick}
                  expandableColumns={['Media', 'ParentSubmissionId']}
                  expandedState={expandedState}
                  renderSubRowComponent={renderSubRowComponent}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </ReusableModal>
  );
};

export default ViewHistory;
